import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from "mobx";
import { Clip } from '../Clip/Clip';
import './ImageClip.styles.scss';
import {CompositionStateStore} from "../../../../store/CompositionStateStore";
import {useAssetStore} from "../../../../../storage/AssetStore";
import {ProjectAssetsStore} from "../../../../../state/ProjectAssetsStore";
import { VisualTransitionConfig } from "../TransitionHandle";
import { CompositionService } from "../../../../services/CompositionService";
import { CompositionEventsEnum } from "../../../../CompositionEvents";

interface ImageClipProps {
    clipId: string;
    layerId: string;
}

export const ImageClip: React.FC<ImageClipProps> = observer(({ clipId, layerId }) => {
    const [filename, setFilename] = useState<string | undefined>(CompositionStateStore.getClipTitle(clipId));
    const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
    const [visualFadeInConfig, setVisualFadeInConfig] = useState<VisualTransitionConfig | undefined>(undefined);
    const [visualFadeOutConfig, setVisualFadeOutConfig] = useState<VisualTransitionConfig | undefined>(undefined);

    const { GetThumbnailUrl } = useAssetStore();

    useEffect(() => {
        const disposer = reaction(
            () => ({
                filename: CompositionStateStore.getClipTitle(clipId),
            }),
            ({ filename }) => {
                setFilename(filename);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, [clipId]);

    useEffect(() => {
        const thumbnailId = CompositionStateStore.clipIdToThumbnailId?.[clipId];
        if (!thumbnailId) return;
        (async () => {
            const thumbnailUrl = await GetThumbnailUrl({ thumbnailId: thumbnailId, projectId: ProjectAssetsStore.projectId as string });
            setThumbnail(thumbnailUrl);
        })();
    }, [clipId]);

    // Sync visual transition config from engine clip
    useEffect(() => {
        const clip = CompositionService.getClipById(clipId) as any;
        if (!clip) return;

        if (clip.visualFadeIn) {
            setVisualFadeInConfig({
                type: clip.visualFadeIn.type ?? 'none',
                durationSec: (clip.visualFadeIn.durationMs ?? 0) / 1000,
                wipeDirection: clip.visualFadeIn.wipeDirection,
                blurAmount: clip.visualFadeIn.blurAmount,
            });
        }
        if (clip.visualFadeOut) {
            setVisualFadeOutConfig({
                type: clip.visualFadeOut.type ?? 'none',
                durationSec: (clip.visualFadeOut.durationMs ?? 0) / 1000,
                wipeDirection: clip.visualFadeOut.wipeDirection,
                blurAmount: clip.visualFadeOut.blurAmount,
            });
        }
    }, [clipId]);

    const handleVisualFadeInChange = useCallback((config: VisualTransitionConfig) => {
        setVisualFadeInConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.visualFadeIn = {
                type: config.type,
                durationMs: config.type === 'none' ? 0 : config.durationSec * 1000,
                wipeDirection: config.wipeDirection,
                blurAmount: config.blurAmount,
            };
            try { clip.trigger?.('frame', undefined); } catch {}
            try { clip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip }); } catch {}
        }
    }, [clipId]);
    
    const handleVisualFadeOutChange = useCallback((config: VisualTransitionConfig) => {
        setVisualFadeOutConfig(config);
        const clip = CompositionService.getClipById(clipId) as any;
        if (clip) {
            clip.visualFadeOut = {
                type: config.type,
                durationMs: config.type === 'none' ? 0 : config.durationSec * 1000,
                wipeDirection: config.wipeDirection,
                blurAmount: config.blurAmount,
            };
            try { clip.trigger?.('frame', undefined); } catch {}
            try { clip.track?.composition?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip }); } catch {}
        }
    }, [clipId]);

    return (
        <Clip 
            clipId={clipId} 
            layerId={layerId} 
            filename={filename} 
            thumbnail={thumbnail} 
            backgroundColor={"169, 221, 191"}
            clipVariant="media"
            clipKind="image"
            showAudioTransitions={false}
            showVisualTransitions={true}
            visualFadeInConfig={visualFadeInConfig}
            visualFadeOutConfig={visualFadeOutConfig}
            onVisualFadeInChange={handleVisualFadeInChange}
            onVisualFadeOutChange={handleVisualFadeOutChange}
        >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M9.5 1.5H2.5C1.94772 1.5 1.5 1.94772 1.5 2.5V9.5C1.5 10.0523 1.94772 10.5 2.5 10.5H9.5C10.0523 10.5 10.5 10.0523 10.5 9.5V2.5C10.5 1.94772 10.0523 1.5 9.5 1.5Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M4.25 5C4.66421 5 5 4.66421 5 4.25C5 3.83579 4.66421 3.5 4.25 3.5C3.83579 3.5 3.5 3.83579 3.5 4.25C3.5 4.66421 3.83579 5 4.25 5Z"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M10.5 7.5L8 5L2.5 10.5"
                    stroke="white"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </Clip>
    );
});

export default ImageClip;
